import { Message } from '@twilio/conversations'
import { useMemo, useState, FC, useEffect } from 'react'
import { isToday } from 'date-fns'

import { getUserName } from 'services/utilFunctions/getUserName'
import { Format } from 'services/Dates'

import { Badge, Button } from 'ui'

import styles from './messageItem.module.scss'

type Props = {
  message: Message
  isAuthor: boolean
  sameDateAsPreviousMessage: boolean
  sameAuthorAsPreviousMessage: boolean
  sameMinuteAsPreviousMessage: boolean
  lessThanMinutesThresholdFromPreviousMessage: boolean
}

const MessageItem: FC<Props> = ({
  sameDateAsPreviousMessage,
  message,
  sameAuthorAsPreviousMessage,
  isAuthor,
  sameMinuteAsPreviousMessage,
  lessThanMinutesThresholdFromPreviousMessage
}) => {
  const [isDeleting, setIsDeleting] = useState(false)
  const shouldShowMessageAuthor = useMemo(() => {
    if (!sameAuthorAsPreviousMessage) return true
    if (lessThanMinutesThresholdFromPreviousMessage) return false

    return true
  }, [sameAuthorAsPreviousMessage, lessThanMinutesThresholdFromPreviousMessage])

  const canDeleteMessage = useMemo(() => {
    if (!message.dateCreated) return false
    return isToday(message.dateCreated)
  }, [message])

  if (!message.dateCreated) return null

  const isMediaMessage = message.attachedMedia && message.attachedMedia?.length > 0

  return (
    <div className={styles.textBubleContainer}>
      {!sameDateAsPreviousMessage && (
        <Badge variant="light" className="align-self-center my-2 py-2">
          {Format(message.dateCreated, { outFormat: 'humanDateSemiCompact' })}{' '}
        </Badge>
      )}
      <div className={`d-flex ${styles.author} ${isAuthor && 'ml-auto'}`}>
        {!isAuthor && shouldShowMessageAuthor && message.author ? <span>{getUserName(message.author)}</span> : null}
        {!sameMinuteAsPreviousMessage || !sameAuthorAsPreviousMessage ? (
          <span className="ml-1">{Format(message.dateCreated, { outFormat: 'time' })}</span>
        ) : null}
      </div>
      <div className="d-flex">
        <span className={`${isAuthor ? 'bg-primary ml-auto' : 'bg-secondary mr-auto'} ${styles.textBuble}`}>
          {isMediaMessage ? <MessageMedia message={message} /> : message.body}
        </span>
        {isAuthor && canDeleteMessage ? (
          <Button
            disabled={isDeleting}
            onClick={async () => {
              setIsDeleting(true)
              await message.remove()
              setIsDeleting(false)
            }}
            className={`btn ${styles.deleteMessage}`}
          >
            <i className="fa-solid fa-xmark"></i>
          </Button>
        ) : null}
      </div>
    </div>
  )
}

export { MessageItem }

const MessageMedia = ({ message }: { message: Message }) => {
  const [Urls, setUrls] = useState<Map<string, string>>(new Map())
  useEffect(() => {
    const getUrl = async () => {
      if (!message.attachedMedia) return
      const urlMaps = await message.getTemporaryContentUrlsForAttachedMedia()
      setUrls(urlMaps)
    }
    getUrl()
  }, [message])

  return (
    <div>
      {message.attachedMedia?.map(media => {
        if (!media) return null
        const href = Urls.get(media.sid)
        if (!href) return null
        return (
          <a style={{ color: 'white' }} key={media.sid} href={href} target="_blank" rel="noreferrer">
            {media.filename}
          </a>
        )
      })}
    </div>
  )
}
